import type { CityConfig } from "@/pages/city-template/types";

export const sierraVistaConfig: CityConfig = {
  city: "Sierra Vista",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "sierra-vista",
  route: "/cities/sierra-vista",
  title: "Emergency & Expedited Passport Services Sierra Vista AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Sierra Vista, AZ — as fast as 24 hours. Fast Passport Center couriers to the Western Passport Center in Tucson, only 75 miles away — the closest major AZ city to a passport agency. A+ BBB rated. Serving Fort Huachuca, Cochise County, and border region travelers.",
  h1: "Emergency & Expedited Passport Services in Sierra Vista, AZ",
  heroSubtitle: "Sierra Vista is Arizona's closest major city to a passport agency — just 75 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Fort Huachuca service member with PCS orders, a defense contractor with international travel, or a Cochise County family planning a vacation.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sierra%20Vista%20Arizona%20high%20desert%20landscape%20with%20Huachuca%20Mountains%20dramatically%20lit%20at%20golden%20sunset%20hour%2C%20sprawling%20desert%20terrain%20with%20rugged%20mountain%20peaks%2C%20warm%20golden%20amber%20light%20across%20the%20San%20Pedro%20Valley%2C%20Southwestern%20United%20States%20landscape%20photography%2C%20wide%20cinematic%20composition%2C%20no%20text%20or%20people%20visible%2C%20natural%20desert%20beauty&width=1800&height=700&seq=sierra-vista-hero-001&orientation=landscape",
  heroImageAlt: "Sierra Vista Arizona high desert with Huachuca Mountains at golden sunset",
  geoPosition: "31.5455;-110.2772",
  geoPlacename: "Sierra Vista, Arizona",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 75 miles from Sierra Vista — about 1 hour 15 minutes via AZ-90 and I-10",
  },
  nearbyCitiesZips: [
    { name: "Sierra Vista", zips: "85635, 85613, 85650" },
    { name: "Fort Huachuca", zips: "85613, 85670" },
    { name: "Hereford", zips: "85615" },
    { name: "Huachuca City", zips: "85616" },
    { name: "Bisbee", zips: "85603" },
    { name: "Tombstone", zips: "85638" },
    { name: "Benson", zips: "85602" },
    { name: "Douglas", zips: "85607, 85608" },
    { name: "Willcox", zips: "85643" },
    { name: "Nogales", zips: "85621" },
    { name: "Patagonia", zips: "85624" },
    { name: "Sonoita", zips: "85637" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — only 75 miles from Sierra Vista" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Closest to Tucson Agency", desc: "Sierra Vista is the nearest major Arizona city to the Western Passport Center — fastest turnaround in the state" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Sierra Vista case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Tucson AZ", route: "/cities/tucson" },
    { name: "Expedited passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Urgent passport in Nogales AZ", route: "/cities/nogales" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Expedited passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Fast passport in Flagstaff AZ", route: "/cities/flagstaff" },
    { name: "Emergency passport in Green Valley AZ", route: "/cities/green-valley" },
  ],
  fpcOfficeAddress: "733 N Stone Ave, Tucson, AZ 85701",
  fpcOfficeNote: "Fast Passport Center serves Sierra Vista remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson — just 75 miles away. This proximity gives Sierra Vista the fastest turnaround time of any Arizona city outside Tucson itself. Military families, defense contractors, and Cochise County residents benefit from the shortest courier route in the state.",
  fpcOfficeDistance: "Serving all of Cochise County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Sierra Vista, Fort Huachuca, and Cochise County. From military service members with PCS orders to border region families, we help southeastern Arizona travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Sierra Vista and nearby communities including Fort Huachuca, Hereford, Huachuca City, Bisbee, Tombstone, Benson, and the entire Cochise County region.",
  howItWorksIntro: "Most Sierra Vista travelers complete everything online and ship documents overnight. At just 75 miles from Tucson — the shortest courier route in Arizona — Fast Passport Center provides the fastest turnaround in the state. Ideal for Fort Huachuca service members on tight PCS timelines and defense contractors with urgent international travel.",
  mistakesIntro: "Sierra Vista travelers, especially Fort Huachuca military personnel and defense contractors facing deployment or PCS moves, encounter unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Sierra Vista and Cochise County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sierra Vista? Here are the most common ones we hear from Fort Huachuca and Cochise County travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Only 75 miles to Tucson (1.25-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Fastest turnaround in Arizona — only 75 miles to Tucson",
        "Ideal for Fort Huachuca PCS and deployment",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from TUS",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Sierra Vista is only 75 miles from the Western Passport Center in Tucson — the closest major AZ city to the agency. Fastest courier turnaround in the state.",
      bullets: ["Fastest AZ turnaround — 75 miles to Tucson", "24–48 hour processing available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Sierra Vista adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Sierra Vista and Cochise County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Sierra Vista and Cochise County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Military family support expertise"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Sierra Vista, Fort Huachuca, and Cochise County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Cochise County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Military family considerations"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Sierra Vista and Fort Huachuca residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Sierra Vista AZ", description: "Urgent passport processing for Sierra Vista, AZ travelers via Western Passport Center courier — only 75 miles from Sierra Vista, fastest turnaround in Arizona." },
    { name: "Passport Renewal Sierra Vista AZ", description: "Fast passport renewal for Sierra Vista, Fort Huachuca, and Cochise County, Arizona residents." },
    { name: "New Passport Sierra Vista AZ", description: "First-time passport application assistance for Sierra Vista and Fort Huachuca residents." },
    { name: "Lost Passport Sierra Vista AZ", description: "Passport replacement for lost or stolen passports for Sierra Vista and Cochise County residents." },
    { name: "Child Passport Sierra Vista AZ", description: "Minor passport services for Sierra Vista and Fort Huachuca military families." },
    { name: "Name Change Passport Sierra Vista AZ", description: "Passport name change service for Sierra Vista, AZ residents." },
  ],
  testimonials: [
    {
      name: "Sgt. James R.",
      location: "Fort Huachuca",
      rating: 5,
      text: "Got PCS orders to Germany with 10 days to out-process. My passport had expired last year. Fast Passport Center understood military timelines and got my passport renewed in 3 days. Made it to my next duty station on time.",
      service: "Military PCS Expedited",
    },
    {
      name: "Karen L.",
      location: "Defense Contractor, Sierra Vista",
      rating: 5,
      text: "Last-minute contract travel to South Korea. My clearance was good but my passport was expired. Fast Passport Center expedited everything — new passport in 4 days. The proximity to Tucson makes a huge difference in turnaround time.",
      service: "Expedited Renewal",
    },
    {
      name: "The Garcia Family",
      location: "Hereford, AZ",
      rating: 5,
      text: "Family reunion in Mexico — 6 of us needed passports including the kids. Fast Passport Center handled all six applications and had them back in 7 days. The short courier distance to Tucson really sped things up.",
      service: "Family Passports",
    },
    {
      name: "Maria H.",
      location: "Bisbee, AZ",
      rating: 5,
      text: "Artist heading to a gallery opening in Mexico City. Lost my passport during a house move. Fast Passport Center got me an emergency replacement in 48 hours. The Tucson proximity is a game changer for Cochise County.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "sv-1",
      name: "Sierra Vista Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2300 E Fry Blvd",
      city: "Sierra Vista",
      state: "AZ",
      zip: "85635",
      phone: "(520) 458-2540",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Central Sierra Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Sierra Vista. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-2",
      name: "Fort Huachuca Military Personnel Division",
      type: "Military Installation Passport Services",
      address: "Building 41401, Christy Ave",
      city: "Fort Huachuca",
      state: "AZ",
      zip: "85613",
      phone: "(520) 533-2000",
      hours: "Mon–Fri: 7:30 AM – 4:00 PM (military personnel only)",
      distance: "Fort Huachuca main post",
      website: "https://home.army.mil/huachuca/",
      websiteLabel: "Fort Huachuca Official Site",
      notes: "Passport services for active duty military and dependents. Check with unit for current availability and requirements. Base access required.",
      icon: "ri-shield-line",
    },
    {
      id: "sv-3",
      name: "Cochise County Clerk of Superior Court — Bisbee",
      type: "County Clerk Passport Acceptance",
      address: "100 Quality Hill Rd",
      city: "Bisbee",
      state: "AZ",
      zip: "85603",
      phone: "(520) 432-9200",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "25 miles southeast in Bisbee",
      website: "https://www.courts.cochise.az.gov/clerk",
      websiteLabel: "Cochise County Clerk",
      notes: "Accepts passport applications for Cochise County residents. Convenient for Bisbee, Tombstone, and southern county residents.",
      icon: "ri-government-line",
    },
    {
      id: "sv-4",
      name: "Benson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "250 S Ocotillo Ave",
      city: "Benson",
      state: "AZ",
      zip: "85602",
      phone: "(520) 586-3941",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles north of Sierra Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Benson and northern Cochise County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-5",
      name: "Douglas Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "601 10th St",
      city: "Douglas",
      state: "AZ",
      zip: "85607",
      phone: "(520) 364-4521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "50 miles southeast of Sierra Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Douglas and southeastern Cochise County residents near the US-Mexico border.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-6",
      name: "Willcox Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 S Curtis Ave",
      city: "Willcox",
      state: "AZ",
      zip: "85643",
      phone: "(520) 384-2811",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "40 miles northeast of Sierra Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Willcox and northeastern Cochise County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "75 miles — 1.25-hr drive, but still need appointment", fpc: "We handle the short 75-mile courier — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking Tucson Is Close Enough to Just Show Up",
      description: "At 75 miles, Sierra Vista is the closest major Arizona city to the Western Passport Center. But even with the short drive, the Tucson agency requires pre-scheduled appointments. Walk-ins are turned away regardless of distance. We handle the appointment and delivery for you — no wasted drive.",
    },
    {
      icon: "ri-shield-line",
      title: "Fort Huachuca PCS Passport Confusion",
      description: "Active duty service members sometimes confuse official/no-fee passports (for PCS moves) with tourist passports (for personal travel). Make sure you know which type you need before starting the application process.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. CVS and Walgreens in Sierra Vista are generally reliable, but always verify — military ID photos use different specifications.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Form for Military Families",
      description: "Military dependents with no-fee passports still need a tourist passport for personal international travel. No-fee passports cannot be used for leisure trips. Make sure you're filing the correct form for your travel purpose.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Orders or Itinerary Ready",
      description: "Emergency processing at the Western Passport Center requires confirmed travel documents. Military personnel should have PCS orders or approved leave forms; civilians should have flight itineraries or cruise bookings.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long — Even with Proximity",
      description: "Despite being only 75 miles from Tucson, Sierra Vista residents using standard USPS processing still wait 8-11 weeks. Military families with PCS orders should start passport applications at least 8 weeks before any scheduled move.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Sierra Vista, Fort Huachuca, or Bisbee." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — only 75 miles away, fastest route in Arizona." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sierra Vista or Cochise County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Sierra Vista from a passport agency?",
      a: "Sierra Vista is only 75 miles from the Western Passport Center in Tucson — the closest major Arizona city to a regional passport agency. The drive takes about 1 hour 15 minutes via AZ-90 and I-10. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles courier delivery for you.",
    },
    {
      q: "Can Fort Huachuca military personnel get fast passports for PCS moves?",
      a: "Yes. Fort Huachuca service members are our most frequent customers in Cochise County. For personal/tourist passports needed alongside PCS orders, we can process in 24–48 hours for emergencies or 2–9 business days for standard expedited service. Note: no-fee official passports for PCS moves are handled through military channels.",
    },
    {
      q: "How fast can I get an emergency passport in Sierra Vista?",
      a: "Emergency processing is typically 24–48 hours for Sierra Vista residents. With only 75 miles to Tucson, we have the fastest courier turnaround in Arizona. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Sierra Vista have its own passport agency?",
      a: "No. The closest regional passport agency is the Western Passport Center in Tucson, just 75 miles away. Sierra Vista has acceptance facilities at the Main Post Office on Fry Blvd and on Fort Huachuca for military personnel.",
    },
    {
      q: "What about defense contractors needing international travel documents?",
      a: "Defense contractors working at Fort Huachuca frequently need expedited passports for international assignments. Fast Passport Center understands security clearance timelines and can synchronize passport processing with your travel requirements. We serve all defense contractors in the Sierra Vista area.",
    },
    {
      q: "Where do Sierra Vista travelers fly internationally?",
      a: "Sierra Vista travelers typically fly internationally through Tucson International Airport (TUS, 75 miles northwest), which offers connections to major hubs. Phoenix Sky Harbor (PHX, 190 miles northwest) offers more extensive international routes. A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Sierra Vista?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Sierra Vista?",
      a: "CVS Pharmacy and Walgreens on Fry Blvd in Sierra Vista offer passport photo services. The Main Post Office on Fry Blvd accepts applications but may not offer photos. Always verify your photos meet the exact 2x2 inch specification — military ID photos use different standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Sierra Vista?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Sierra Vista Main Post Office or Cochise County Clerk in Bisbee.",
    },
    {
      q: "Can you serve the entire Cochise County region?",
      a: "Yes. We serve all of Cochise County including Sierra Vista, Fort Huachuca, Hereford, Huachuca City, Bisbee, Tombstone, Benson, Douglas, Willcox, and surrounding communities. Documents ship overnight from any UPS location in the area.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Sierra Vista", address: "3750 E Fry Blvd, Sierra Vista, AZ 85635", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Sierra Vista", address: "85 S Hwy 92, Sierra Vista, AZ 85635", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Walgreens — Sierra Vista (Fry Blvd)", address: "2100 E Fry Blvd, Sierra Vista, AZ 85635", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Benson", address: "250 S Ocotillo Ave, Benson, AZ 85602", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Bisbee", address: "101 Naco Rd, Bisbee, AZ 85603", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Sierra Vista", address: "2120 E Fry Blvd, Ste B, Sierra Vista, AZ 85635", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Sierra Vista", address: "2100 E Fry Blvd, Sierra Vista, AZ 85635", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Cochise County residents. Closest MVD to Fort Huachuca.", featured: true },
    { name: "AZ MVD — Tucson (East)", address: "1360 S Stocker Dr, Tucson, AZ 85710", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Full-service MVD for southeastern Arizona residents. About 75 miles northwest.", featured: false },
    { name: "AZ MVD — Douglas", address: "1100 F Ave, Douglas, AZ 85607", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Douglas and border region residents of Cochise County.", featured: false },
  ],
  airports: [
    {
      code: "FHU",
      name: "Sierra Vista Municipal Airport / Libby Army Airfield",
      distance: "3 miles northwest of downtown Sierra Vista",
      address: "2100 Airport Ave, Sierra Vista, AZ 85635",
      note: "Joint-use civilian-military airport serving Sierra Vista and Fort Huachuca. Limited commercial service — primarily general aviation and military operations. Most international travel connects through TUS.",
      flightUrl: "https://www.sierravistaaz.gov/airport",
      flightLabel: "FHU Airport",
      featured: true,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "75 miles northwest of Sierra Vista",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Closest full-service commercial airport with domestic and limited international routes. About 1.25 hours via AZ-90 and I-10. Co-located with the Western Passport Center.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "190 miles northwest of Sierra Vista",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 3 hours northwest via I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
  ],
};